// E8MixedNumberExpressionClosedTask.js
// Zadanie zamkniete E8: dzialania laczne na liczbach wymiernych.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function lcm(a, b) {
  return Math.abs(a * b) / gcd(a, b);
}

function fraction(num, den = 1) {
  const sign = den < 0 ? -1 : 1;
  const divisor = gcd(num, den);
  return {
    num: (sign * num) / divisor,
    den: Math.abs(den) / divisor,
  };
}

function subtract(a, b) {
  return fraction(a.num * b.den - b.num * a.den, a.den * b.den);
}

function divide(a, b) {
  return fraction(a.num * b.den, a.den * b.num);
}

function negate(value) {
  return fraction(-value.num, value.den);
}

function valueKey(value) {
  return `${value.num}/${value.den}`;
}

function fractionLatex(value) {
  const sign = value.num < 0 ? "-" : "";
  const absNum = Math.abs(value.num);
  if (value.den === 1) return `${value.num}`;

  const whole = Math.floor(absNum / value.den);
  const remainder = absNum % value.den;
  if (!whole) return `${sign}\\frac{${remainder}}{${value.den}}`;
  if (!remainder) return `${sign}${whole}`;
  return `${sign}${whole}\\dfrac{${remainder}}{${value.den}}`;
}

function improperFractionLatex(value) {
  if (value.den === 1) return `${value.num}`;
  const sign = value.num < 0 ? "-" : "";
  return `${sign}\\frac{${Math.abs(value.num)}}{${value.den}}`;
}

function wrappedFractionLatex(value) {
  const latex = improperFractionLatex(value);
  return value.num < 0 ? `\\left(${latex}\\right)` : latex;
}

function decimalLatex(tenths) {
  return (tenths / 10).toFixed(1).replace(".", "{,}");
}

function mixedLatex(whole, num, den) {
  return `${whole}\\frac{${num}}{${den}}`;
}

function signedTermLatex(latex, sign) {
  return sign < 0 ? `\\left(-${latex}\\right)` : latex;
}

function shuffle(values) {
  const result = [...values];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function createOptions(result) {
  const absoluteNumerator = Math.abs(result.num);
  const nearbyCandidates = [absoluteNumerator - 1, absoluteNumerator + 1]
    .filter((numerator) => numerator > result.den)
    .filter((numerator) => gcd(numerator, result.den) === 1);
  if (!nearbyCandidates.length) return null;

  const nearbyNumerator = pick(nearbyCandidates);
  const nearby = fraction(
    Math.sign(result.num) * nearbyNumerator,
    result.den
  );
  const values = shuffle([result, negate(result), nearby, negate(nearby)]);

  return {
    options: values.map((value, index) => ({
      label: LETTERS[index],
      latex: fractionLatex(value),
      value,
    })),
    correctIndex: values.findIndex((value) => valueKey(value) === valueKey(result)),
  };
}

function createOriginalTask() {
  const result = fraction(22, 15);
  const optionValues = [
    fraction(-23, 15),
    fraction(-22, 15),
    result,
    fraction(23, 15),
  ];

  return {
    type: "E8MixedNumberExpressionClosedTask",
    data: {
      decimalTenths: 24,
      mixedWhole: 5,
      mixedNum: 1,
      mixedDen: 3,
      decimalSign: 1,
      mixedSign: 1,
      decimalFirst: true,
      divisor: 2,
      first: fraction(12, 5),
      second: fraction(16, 3),
      difference: fraction(-44, 15),
      result,
      options: optionValues.map((value, index) => ({
        label: LETTERS[index],
        latex: fractionLatex(value),
        value,
      })),
      correctIndex: 2,
    },
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 2,
    },
    tags: ["e8", "liczby_wymierne", "ulamki", "kolejnosc_dzialan"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  for (let attempt = 0; attempt < 300; attempt += 1) {
    const decimalTenths = pick([12, 14, 16, 18, 22, 24, 26, 28, 32, 34]);
    const mixedWhole = pick([3, 4, 5, 6]);
    // Ułamek dziesiętny skraca się do mianownika 5. Drugi mianownik ma być
    // inny, aby odejmowanie zawsze wymagało sprowadzenia do wspólnego mianownika.
    const mixedDen = pick([3, 4, 6]);
    const mixedNum = pick(
      Array.from({ length: mixedDen - 1 }, (_, index) => index + 1).filter(
        (value) => gcd(value, mixedDen) === 1
      )
    );
    const divisor = pick([2, 3, 4, 5]);

    const [decimalSign, mixedSign] = pick([
      [-1, -1],
      [-1, 1],
      [1, -1],
      [1, 1],
    ]);

    const decimal = fraction(decimalSign * decimalTenths, 10);
    const mixed = fraction(
      mixedSign * (mixedWhole * mixedDen + mixedNum),
      mixedDen
    );
    // Dla dwoch dodatnich liczb odejmujemy mniejsza od wiekszej.
    const decimalFirst =
      decimalSign > 0 && mixedSign > 0
        ? decimal.num * mixed.den < mixed.num * decimal.den
        : Math.random() < 0.5;
    const first = decimalFirst ? decimal : mixed;
    const second = decimalFirst ? mixed : decimal;
    const difference = subtract(first, second);
    if (difference.num === 0) continue;

    const result = divide(difference, fraction(-divisor));
    if (
      result.den === 1 ||
      result.den > 40 ||
      Math.abs(result.num) <= result.den ||
      Math.abs(result.num) > result.den * 6
    ) {
      continue;
    }

    const optionSet = createOptions(result);
    if (!optionSet) continue;
    const { options, correctIndex } = optionSet;
    return {
      type: "E8MixedNumberExpressionClosedTask",
      data: {
        decimalTenths,
        mixedWhole,
        mixedNum,
        mixedDen,
        decimalSign,
        mixedSign,
        decimalFirst,
        divisor,
        first,
        second,
        difference,
        result,
        options,
        correctIndex,
      },
      source: { variant: "random", basedOn: "E8_2025_MAJ_2" },
      tags: ["e8", "liczby_wymierne", "ulamki", "kolejnosc_dzialan"],
    };
  }

  const result = fraction(-58, 15);
  const { options, correctIndex } = createOptions(result);
  return {
    type: "E8MixedNumberExpressionClosedTask",
    data: {
      decimalTenths: 24,
      mixedWhole: 5,
      mixedNum: 1,
      mixedDen: 3,
      decimalSign: 1,
      mixedSign: -1,
      decimalFirst: true,
      divisor: 2,
      first: fraction(12, 5),
      second: fraction(-16, 3),
      difference: fraction(116, 15),
      result,
      options,
      correctIndex,
    },
    source: { variant: "random", basedOn: "E8_2025_MAJ_2" },
    tags: ["e8", "liczby_wymierne", "ulamki", "kolejnosc_dzialan"],
  };
}

function expressionLatex(data) {
  const decimal = signedTermLatex(
    decimalLatex(data.decimalTenths),
    data.decimalSign
  );
  const mixed = signedTermLatex(
    mixedLatex(data.mixedWhole, data.mixedNum, data.mixedDen),
    data.mixedSign
  );
  const first = data.decimalFirst ? decimal : mixed;
  const second = data.decimalFirst ? mixed : decimal;
  return `\\left(${first}-${second}\\right):(-${data.divisor})`;
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>Dane jest wyrażenie</p>
      <BlockMath math={expressionLatex(data)} />
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>Wartość tego wyrażenia jest równa</p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "8px",
        }}
      >
        {data.options.map((option) => (
          <div key={option.label}>
            <ChoiceOptionMath letter={option.label} math={option.latex} display />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;
  const letterOptions = data.options.map((option, index) => ({
    value: index,
    label: option.label,
    textNode: option.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={(choice) => {
        const index =
          typeof choice === "number"
            ? choice
            : Number(choice?.target?.value ?? choice?.value);
        const option = data.options[index];
        onChange({
          choiceIndex: index,
          label: option?.label,
          latex: option?.latex,
        });
      }}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer?.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const option = data.options[data.correctIndex];
  return (
    <span>
      <ChoiceOptionMath letter={option.label} math={option.latex} display />
    </span>
  );
}

export function renderUserAnswer(userAnswer, task) {
  const option = task?.data?.options?.[userAnswer?.choiceIndex];
  return option ? (
    <span>
      <ChoiceOptionMath letter={option.label} math={option.latex} display />
    </span>
  ) : (
    <i>(brak odpowiedzi)</i>
  );
}

export function renderExplanation({ data }) {
  const decimal = fraction(data.decimalSign * data.decimalTenths, 10);
  const mixed = fraction(
    data.mixedSign * (data.mixedWhole * data.mixedDen + data.mixedNum),
    data.mixedDen
  );
  const decimalSource = signedTermLatex(
    decimalLatex(data.decimalTenths),
    data.decimalSign
  );
  const mixedSource = signedTermLatex(
    mixedLatex(data.mixedWhole, data.mixedNum, data.mixedDen),
    data.mixedSign
  );
  const decimalTenthsNumerator = data.decimalSign * data.decimalTenths;
  const commonDenominator = lcm(data.first.den, data.second.den);
  const firstExpandedNumerator =
    data.first.num * (commonDenominator / data.first.den);
  const secondExpandedNumerator =
    data.second.num * (commonDenominator / data.second.den);
  const rawDivisionNumerator = data.difference.num * -1;
  const rawDivisionDenominator = data.difference.den * data.divisor;
  const correctOption = data.options[data.correctIndex];

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Zamieniamy ułamek dziesiętny na ułamek zwykły
        i skracamy go.
      </p>
      <BlockMath
        math={`${decimalSource}=\\frac{${decimalTenthsNumerator}}{10}=${improperFractionLatex(
          decimal
        )}`}
      />
      <p>
        <strong>Krok 2.</strong> Liczbę mieszaną zamieniamy na ułamek
        niewłaściwy.
      </p>
      <BlockMath
        math={`${mixedSource}=\\frac{${data.mixedSign < 0 ? "-" : ""}\\left(${data.mixedWhole}\\cdot${data.mixedDen}+${data.mixedNum}\\right)}{${data.mixedDen}}=${improperFractionLatex(
          mixed
        )}`}
      />
      <p>
        <strong>Krok 3.</strong> Sprowadzamy ułamki w nawiasie do wspólnego
        mianownika <InlineMath math={`${commonDenominator}`} /> i wykonujemy
        odejmowanie.
      </p>
      <BlockMath
        math={`${improperFractionLatex(data.first)}-${wrappedFractionLatex(
          data.second
        )}=\\frac{${firstExpandedNumerator}}{${commonDenominator}}-\\left(\\frac{${secondExpandedNumerator}}{${commonDenominator}}\\right)=${improperFractionLatex(
          data.difference
        )}`}
      />
      <p>
        <strong>Krok 4.</strong> Dzielenie przez{" "}
        <InlineMath math={`-${data.divisor}`} /> zastępujemy mnożeniem przez
        liczbę odwrotną <InlineMath math={`-\\frac{1}{${data.divisor}}`} />.
      </p>
      <BlockMath
        math={`${improperFractionLatex(
          data.difference
        )}:(-${data.divisor})=${wrappedFractionLatex(
          data.difference
        )}\\cdot\\left(-\\frac{1}{${data.divisor}}\\right)=\\frac{${rawDivisionNumerator}}{${rawDivisionDenominator}}=${improperFractionLatex(
          data.result
        )}=${fractionLatex(data.result)}`}
      />
      <p>
        <strong>Krok 5.</strong> Porównujemy wynik z podanymi odpowiedziami.
        Poprawna jest odpowiedź{" "}
        <strong>
          <InlineMath
            math={`${correctOption.label}.\\ ${correctOption.latex}`}
          />
        </strong>
        .
      </p>
    </div>
  );
}
